import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2, 
  Calendar, 
  Clock, 
  Users, 
  Mail,
  Utensils,
  Home,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmationPage() {
  const { reservationId } = useParams();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReservation();
  }, [reservationId]);

  const fetchReservation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}`);
      if (response.ok) {
        const data = await response.json();
        setReservation(data);
      } else {
        setError('Réservation non trouvée');
      }
    } catch (err) {
      console.error('Failed to fetch reservation:', err);
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-600 mb-4">{error}</p>
          <Link to="/">
            <Button>Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <Utensils className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span className="font-bold text-xl text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                L'Ange Di Vin
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 md:py-20">
        <div className="max-w-xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl border border-zinc-200 p-8 text-center"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </motion.div>

            <h1 
              className="text-3xl font-bold text-zinc-900 mb-3"
              style={{ fontFamily: 'Manrope' }}
              data-testid="confirmation-title"
            >
              Réservation confirmée !
            </h1>
            <p className="text-zinc-600 mb-8">
              Un email de confirmation a été envoyé à<br />
              <span className="font-medium text-zinc-900">{reservation.guest_email}</span>
            </p>

            {/* Reservation Details */}
            <div className="bg-zinc-50 rounded-xl p-6 mb-8 text-left">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-zinc-500">
                    <Calendar className="w-5 h-5" />
                    Date
                  </span>
                  <span className="font-medium text-zinc-900">
                    {format(new Date(reservation.date), 'EEEE d MMMM yyyy', { locale: fr })}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-zinc-500">
                    <Clock className="w-5 h-5" />
                    Heure
                  </span>
                  <span className="font-medium text-zinc-900">
                    {reservation.time_slot}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-zinc-500">
                    <Users className="w-5 h-5" />
                    Personnes
                  </span>
                  <span className="font-medium text-zinc-900">
                    {reservation.party_size} {reservation.party_size === 1 ? 'personne' : 'personnes'}
                  </span>
                </div>

                <div className="pt-4 border-t border-zinc-200">
                  <p className="text-sm text-zinc-500">Numéro de réservation</p>
                  <p className="font-mono text-orange-500 font-medium" data-testid="reservation-id">
                    {reservation.reservation_id}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Info */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg mb-8 text-left">
              <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Conservez votre numéro de réservation. 
                Il vous sera demandé à votre arrivée au restaurant.
              </p>
            </div>

            {/* Back Home Button */}
            <Link to="/">
              <Button 
                className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full h-12 px-8"
                data-testid="back-home-button"
              >
                <Home className="w-5 h-5 mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
